import React, { useState } from "react";
import { Layout, Spin, Typography } from "antd";
import { WifiSlash, ArrowsClockwise } from "@phosphor-icons/react";
import { SlideMenu, MainContent } from "./view";
import { useQuery } from "react-query";
import { isLoggedIn as isUserLogin } from "./service/client";
import { Footer, Header } from "./components/organisms";
import { useLocation, Routes, Route, Navigate } from "react-router-dom";
import { useResponsive } from "./hooks/useResponsive";
import { UnsavedChangesProvider } from "./contexts/UnsavedChangesContext";
import { BranchProvider, useBranchContext } from "./contexts/branch-context";
import { useOrdersSocket, SocketStatus } from "./hooks/useOrdersSocket";
import { useImpersonation } from "./contexts/impersonation-context/ImpersonationContext";

// Import nowych komponentów autoryzacji
import { LoginFormNew } from "./components/organisms/login-form/LoginFormNew";
import { RegisterFormNew } from "./components/organisms/register-form/RegisterFormNew";
import { PasswordResetRequestNew } from "./components/organisms/password-reset/PasswordResetRequestNew";
import { PasswordResetConfirmNew } from "./components/organisms/password-reset/PasswordResetConfirmNew";
import { EmailVerification } from "./components/organisms/email-verification/EmailVerification";
import { ResendVerificationEmailNew } from "./components/organisms/resend-verification/ResendVerificationEmailNew";
import { AccountDeleted } from "./components/organisms/account-deleted/AccountDeleted";
import { RegisterSuccess } from "./components/organisms/register-success/RegisterSuccess";
import { DeveloperPanel } from "./components/pages/DeveloperPanel";
import { ImpersonationFrame } from "./components/organisms/impersonation-frame/ImpersonationFrame";
import { Onboarding } from "./components/pages/Onboarding";
import { getCompany } from "./service/client";
import Cookies from "js-cookie";
import { TourProvider } from "./contexts/tour-context/TourProvider";
import { AdditionsTourProvider } from "./contexts/additions-tour-context/AdditionsTourProvider";
import { ScrollToTop } from "./components/atoms/ScrollToTop";
import i18n from "./i18n";

const CONNECTION_STATUS_CONFIG: Record<Exclude<SocketStatus, "connected">, {
  icon: React.ReactNode;
  title: string;
  description: string;
}> = {
  offline: {
    icon: <WifiSlash size={48} weight="duotone" />,
    title: i18n.t("app.connectionLostInternetTitle"),
    description: i18n.t("app.connectionRetryingDescription"),
  },
  disconnected: {
    icon: <WifiSlash size={48} weight="duotone" />,
    title: i18n.t("app.connectionLostServerTitle"),
    description: i18n.t("app.connectionRetryingDescription"),
  },
  connecting: {
    icon: <ArrowsClockwise size={48} weight="duotone" />,
    title: i18n.t("app.connectingToServerTitle"),
    description: i18n.t("app.connectingInProgressDescription"),
  },
};

const getConnectionConfig = (status: SocketStatus) => {
  if (status === "offline") {
    return {
      icon: <WifiSlash size={48} weight="duotone" />,
      title: i18n.t("app.noInternetConnectionTitle"),
      description: i18n.t("app.checkInternetConnectionDescription"),
      color: "var(--color-error, #ff4d4f)",
    };
  }

  if (status === "disconnected") {
    return {
      icon: <WifiSlash size={48} weight="duotone" />,
      title: i18n.t("app.connectionLostServerTitle"),
      description: i18n.t("app.cannotConnectServerDescription"),
      color: "var(--color-error, #ff4d4f)",
    };
  }

  if (status === "connecting") {
    return {
      icon: <ArrowsClockwise size={48} weight="duotone" />,
      title: i18n.t("app.connectingToServerTitle"),
      description: i18n.t("app.connectingInProgressDescriptionDot"),
      color: "var(--color-primary, #1677ff)",
    };
  }

  return null; // Jeśli status jest "connected", nie pokazuj nic
};

const SocketInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentBranch } = useBranchContext();
  const status = useOrdersSocket(currentBranch?.id);
  const config = getConnectionConfig(status);

  return (
    <>
      {children}
      {config && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backdropFilter: "blur(6px)",
            background: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              background: "var(--color-surface, #fff)",
              borderRadius: 16,
              padding: "40px 48px",
              textAlign: "center",
              maxWidth: 400,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div style={{ color: config.color, marginBottom: 16 }}>
              {config.icon}
            </div>
            <Typography.Title level={4} style={{ margin: "0 0 8px" }}>
              {config.title}
            </Typography.Title>
            <Typography.Text type="secondary">
              {config.description}
            </Typography.Text>
            {status === "connecting" && (
              <div style={{ marginTop: 20 }}>
                <Spin />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};



const OnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentBranch } = useBranchContext();
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  const { data: company, isLoading: companyLoading } = useQuery(["/company"], getCompany, {
    enabled: !!Cookies.get("accessToken"),
    retry: false,
  });

  if (companyLoading || !currentBranch) {
    return (
      <div style={{ width: "100%", height: "100vh", background: "#1E363C" }} />
    );
  }

  // Check if onboarding is needed: any of the required fields missing
  const needsSlug = !company?.slug;
  const needsName = !currentBranch?.name;
  const needsAddress = !currentBranch?.formattedAddress && !currentBranch?.adress;
  const needsOnboarding = (needsSlug || needsName || needsAddress) && !onboardingComplete;

  if (needsOnboarding) {
    return <Onboarding onComplete={() => setOnboardingComplete(true)} />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const { isMobile } = useResponsive();
  const { isDeveloper, isImpersonating, impersonatedCompany } = useImpersonation();

  const { data: isLoggedIn, refetch, isFetching } = useQuery<any, Error>(
    ["/is-logged-in"],
    () => isUserLogin(),
    {
      retry: false,
      onError: () => {},
    }
  );

  // Public routes that must render immediately without waiting for auth check
  const PUBLIC_PATHS = ["/login", "/register", "/success_register", "/reset-password", "/reset-password-confirm", "/verify-email", "/account-deleted", "/resend-verification"];
  const isPublicPath = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p)) || location.pathname === "/";

  // Timeout fallback: if auth check takes > 8s, unblock UI (treat as not logged in)
  const [authCheckTimedOut, setAuthCheckTimedOut] = React.useState(false);
  React.useEffect(() => {
    if (!isFetching) {
      setAuthCheckTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setAuthCheckTimedOut(true), 8000);
    return () => clearTimeout(timer);
  }, [isFetching]);

  // Dynamicznie kontroluj overflow na <html> w zależności od stanu logowania
  React.useEffect(() => {
    const htmlElement = document.documentElement;
    if (isLoggedIn || location.pathname.includes("/check-order/")) {
      // Dla zalogowanej aplikacji - ukryj overflow (sidebar z własnym scrollem)
      htmlElement.style.overflow = "hidden";
    } else {
      // Dla ekranów autoryzacji - pozwól na scroll
      htmlElement.style.overflow = "auto";
    }

    // Cleanup przy unmount
    return () => {
      htmlElement.style.overflow = "auto";
    };
  }, [isLoggedIn, location.pathname]);

  // Loading state: show dark screen only for non-public routes and only until timeout
  if (isFetching && !isPublicPath && !authCheckTimedOut) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          background: "#1E363C",
        }}
      />
    );
  }

  // Zalogowany użytkownik - główna aplikacja
  if (isLoggedIn || location.pathname.includes("/check-order/")) {
    // Developer without impersonation - show developer panel
    if (isDeveloper && !isImpersonating) {
      return <DeveloperPanel />;
    }

    return (
      <BranchProvider>
        <TourProvider>
          <AdditionsTourProvider>
            <OnboardingGate>
              <SocketInitializer>
                <UnsavedChangesProvider>
                  {/* Impersonation frame - visible only when developer is impersonating */}
                  {isDeveloper && isImpersonating && <ImpersonationFrame />}

                  <Layout hasSider={!isMobile}>
                    {isLoggedIn && (
                      <SlideMenu
                        refetch={refetch}
                        collapsed={collapsed}
                        setCollapsed={setCollapsed}
                      />
                    )}
                    <Layout
                      id="main-scroll-container"
                      style={
                        isLoggedIn
                          ? {
                            minHeight: "100vh",
                            maxHeight: "100vh",
                            overflowY: "auto",
                            overflowX: "hidden",
                            marginLeft: isMobile ? 0 : (collapsed ? 80 : 200),
                            minWidth: 0,
                            paddingTop: isMobile ? "60px" : 0,
                          }
                          : {
                            minHeight: "100vh",
                            padding: 20,
                            minWidth: "100%",
                          }
                      }
                    >
                      {isLoggedIn && <Header />}
                      <ScrollToTop />
                      <MainContent />
                      {isLoggedIn && <Footer />}
                    </Layout>
                  </Layout>
                </UnsavedChangesProvider>
              </SocketInitializer>
            </OnboardingGate>
          </AdditionsTourProvider>
        </TourProvider>
      </BranchProvider>
    );
  }

  // Niezalogowany - ekrany autoryzacji z routingiem
  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/" element={<LoginFormNew refetch={refetch} />} />
        <Route path="/login" element={<LoginFormNew refetch={refetch} />} />
        <Route path="/register" element={<RegisterFormNew />} />
        <Route path="/success_register" element={<RegisterSuccess />} />
        <Route path="/reset-password" element={<PasswordResetRequestNew />} />
        <Route path="/reset-password-confirm" element={<PasswordResetConfirmNew />} />
        <Route path="/verify-email" element={<EmailVerification token={new URLSearchParams(location.search).get("token") || ""} refetch={refetch} />} />
        <Route path="/account-deleted" element={<AccountDeleted />} />
        <Route path="/resend-verification" element={<ResendVerificationEmailNew />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return null;
};

export default App;
